// API utilities for backend communication

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Helper to make authenticated requests
const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Token expired or invalid
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    const err = new Error(error.detail || 'Request failed');
    err.response = { data: error };  // Add response.data for axios-style error handling
    throw err;
  }

  const data = await response.json();
  // Return axios-style response format
  return { data };
};

export const api = {
  // Plans API
  plans: {
    getAll: async () => {
      return authenticatedFetch(`${API_URL}/api/plans`);
    },

    getById: async (id) => {
      return authenticatedFetch(`${API_URL}/api/plans/${id}`);
    },

    create: async (planData) => {
      return authenticatedFetch(`${API_URL}/api/plans`, {
        method: 'POST',
        body: JSON.stringify(planData),
      });
    },

    update: async (id, planData) => {
      return authenticatedFetch(`${API_URL}/api/plans/${id}`, {
        method: 'PUT',
        body: JSON.stringify(planData),
      });
    },

    delete: async (id) => {
      return authenticatedFetch(`${API_URL}/api/plans/${id}`, {
        method: 'DELETE',
      });
    },
  },

  // User Values API
  values: {
    get: async () => {
      return authenticatedFetch(`${API_URL}/api/user-values`);
    },

    update: async (values) => {
      return authenticatedFetch(`${API_URL}/api/user-values`, {
        method: 'POST',
        body: JSON.stringify({ values }),
      });
    },
  },

  // Generic HTTP methods
  get: async (endpoint) => {
    return authenticatedFetch(`${API_URL}${endpoint}`);
  },

  post: async (endpoint, data) => {
    return authenticatedFetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  put: async (endpoint, data) => {
    return authenticatedFetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (endpoint) => {
    return authenticatedFetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
    });
  },
};
